import { useCallback, useEffect, useMemo, useState } from "react";
import { Pressable, ScrollView, TextInput } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import type { ScenarioRow } from "../utils/scenario-group";
import type { ScenarioConfigItem } from "../hooks/use-scenario-configs";
import { usePutScenarioConfig } from "../hooks/use-scenario-configs";
import { formatBearing } from "../utils/format-bearing";
import { MapCanvas } from "./MapCanvas.web";

interface ScenarioMapProps {
  rows: ScenarioRow[];
  configByUuid: Record<string, ScenarioConfigItem>;
  tenantId?: string;
  selectedScenarioUuid?: string | null;
  onScenarioSelect: (scenarioUuid: string) => void;
}

export function ScenarioMap({
  rows,
  configByUuid,
  tenantId,
  selectedScenarioUuid,
  onScenarioSelect,
}: ScenarioMapProps) {
  const { theme } = useUnistyles();
  const { putConfig, isPending } = usePutScenarioConfig();

  const positioned = rows.filter((r) => configByUuid[r.scenarioUuid]?.coordinate);
  const unpositioned = rows.filter((r) => !configByUuid[r.scenarioUuid]?.coordinate);

  const [editingUuid, setEditingUuid] = useState<string | null>(null);
  const [draftLat, setDraftLat] = useState("");
  const [draftLng, setDraftLng] = useState("");
  const [draftDir, setDraftDir] = useState("0");
  const [draftName, setDraftName] = useState("");

  const beginEdit = (row: ScenarioRow) => {
    setEditingUuid(row.scenarioUuid);
    const cfg = configByUuid[row.scenarioUuid];
    setDraftLat(cfg?.coordinate ? cfg.coordinate.lat.toFixed(6) : "");
    setDraftLng(cfg?.coordinate ? cfg.coordinate.lng.toFixed(6) : "");
    // Default bearing seeding rules:
    //   - existing direction → show it
    //   - no coordinate yet (truly new pin) → seed "0" so the arrow appears
    //     pointing north the moment the user clicks the map
    //   - already pinned but no bearing → leave blank; saving must not
    //     silently introduce `direction: 0` for a scenario that never had one
    if (cfg?.direction !== undefined) {
      setDraftDir(formatBearing(cfg.direction));
    } else if (!cfg?.coordinate) {
      setDraftDir("0");
    } else {
      setDraftDir("");
    }
    setDraftName(cfg?.friendlyName ?? "");
  };

  const cancelEdit = () => {
    setEditingUuid(null);
    setDraftLat("");
    setDraftLng("");
    setDraftDir("");
    setDraftName("");
  };

  const saveEdit = async () => {
    if (!editingUuid) return;
    const latTrimmed = draftLat.trim();
    const lngTrimmed = draftLng.trim();
    const dirTrimmed = draftDir.trim();
    const nameTrimmed = draftName.trim();
    const bothEmpty = latTrimmed === "" && lngTrimmed === "";
    const bothFilled = latTrimmed !== "" && lngTrimmed !== "";
    if (!bothEmpty && !bothFilled) return;
    // Direction is only meaningful when the scenario has a coordinate.
    //   - bothFilled + dir typed → save normalised value
    //   - bothFilled + dir blank → clear server-side (`null`) so an empty
    //     input doesn't silently keep a stale bearing
    //   - bothEmpty (coord cleared) → also clear direction; the backend
    //     enforces this invariant defensively, but sending it explicitly
    //     keeps the optimistic UI consistent.
    let directionPatch: number | null | undefined;
    if (bothFilled) {
      if (dirTrimmed === "") {
        directionPatch = null;
      } else {
        const dir = Number.parseFloat(dirTrimmed);
        if (!Number.isFinite(dir)) return;
        const normalised = ((dir % 360) + 360) % 360;
        directionPatch = normalised;
      }
    } else if (bothEmpty) {
      directionPatch = null;
    }
    try {
      const row = rows.find((r) => r.scenarioUuid === editingUuid);
      if (!row) return;
      if (bothEmpty) {
        const existing = configByUuid[editingUuid];
        const hadCoordinate = existing?.coordinate !== undefined;
        const hadFriendlyName = (existing?.friendlyName ?? "").trim() !== "";
        const hadDirection = existing?.direction !== undefined;
        if (!hadCoordinate && !hadFriendlyName && !hadDirection && nameTrimmed === "") return;
        await putConfig(row.siteId, row.assetCameraKey, editingUuid, {
          coordinate: hadCoordinate ? null : undefined,
          direction: hadDirection ? null : undefined,
          friendlyName: nameTrimmed || null,
        });
      } else {
        const lat = Number.parseFloat(latTrimmed);
        const lng = Number.parseFloat(lngTrimmed);
        if (Number.isNaN(lat) || Number.isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) return;
        const existing = configByUuid[editingUuid];
        const hadName = (existing?.friendlyName ?? "").trim() !== "";
        const hadDirection = existing?.direction !== undefined;
        // Only emit a `direction: null` clear when there *is* something to
        // clear — saves us a no-op REMOVE on brand-new pins.
        const directionPayload: Record<string, number | null> = {};
        if (directionPatch !== undefined && (directionPatch !== null || hadDirection)) {
          directionPayload["direction"] = directionPatch;
        }
        await putConfig(row.siteId, row.assetCameraKey, editingUuid, {
          coordinate: { lat, lng },
          ...directionPayload,
          friendlyName: nameTrimmed || (hadName ? null : undefined),
        });
      }
      cancelEdit();
    } catch {
      // Panel stays open so the user can retry; usePutScenarioConfig.error
      // surfaces the failure message to the UI if wired.
    }
  };

  const editingRow = useMemo(
    () => (editingUuid ? (rows.find((r) => r.scenarioUuid === editingUuid) ?? null) : null),
    [editingUuid, rows],
  );

  useEffect(() => {
    if (editingUuid !== null && editingRow === null) cancelEdit();
  }, [editingUuid, editingRow]);

  const handleMapClick = useCallback((lngLat: { lat: number; lng: number }) => {
    setDraftLat(lngLat.lat.toFixed(6));
    setDraftLng(lngLat.lng.toFixed(6));
    // Don't touch `draftDir` here. `beginEdit` is the single place we seed
    // defaults (`"0"` for unpinned, the saved bearing otherwise). If the
    // user has explicitly cleared the input to drop the bearing, repositioning
    // the pin must preserve that intent — overwriting `""` with `"0"` would
    // turn an intended `direction: null` save into a save of `0`.
  }, []);

  const handleDirectionDrag = useCallback((degrees: number) => {
    setDraftDir(formatBearing(degrees));
  }, []);

  // The draft pin shown on the map *immediately* after the user clicks,
  // independent of the saved scenario config. The bearing visual must mirror
  // what `saveEdit` will persist: a blank input means "no bearing" (the save
  // sends `direction: null` to clear it), so the draft pin renders without
  // an arrow rather than misleadingly pointing north.
  const draftPin = useMemo(() => {
    if (!editingUuid) return null;
    const lat = Number.parseFloat(draftLat);
    const lng = Number.parseFloat(draftLng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    const trimmed = draftDir.trim();
    if (trimmed === "") return { coordinate: { lat, lng } };
    const dir = Number.parseFloat(trimmed);
    if (!Number.isFinite(dir)) return { coordinate: { lat, lng } };
    const safeDir = ((dir % 360) + 360) % 360;
    return { coordinate: { lat, lng }, direction: safeDir };
  }, [editingUuid, draftLat, draftLng, draftDir]);

  return (
    <View flex={1} style={{ minHeight: 0, gap: theme.spacing.lg }}>
      <MapCanvas
        rows={rows}
        configByUuid={configByUuid}
        tenantId={tenantId}
        selectedScenarioUuid={selectedScenarioUuid}
        onScenarioSelect={onScenarioSelect}
        onMapClick={editingUuid ? handleMapClick : undefined}
        draftPin={draftPin}
        onDraftDirectionChange={editingUuid ? handleDirectionDrag : undefined}
        suppressedScenarioUuid={draftPin ? editingUuid : null}
      />

      {/* Editing banner */}
      {editingRow && (
        <View
          style={{
            padding: theme.spacing["3xl"],
            borderRadius: theme.spacing.md,
            borderWidth: 1,
            borderColor: theme.colors.borderCard,
            backgroundColor: theme.colors.surfaceCard,
            gap: theme.spacing.lg,
          }}
        >
          <Text textStyle="sm/semibold" color="text">
            Configure — {editingRow.backendScenarioName}
          </Text>
          <Text textStyle="xs/regular" color="textMuted">
            Click anywhere on the map to drop a pin, or type latitude and longitude below. The friendly name is what
            shows everywhere in this app for this scenario. Drag the white arrow on the pin to set the direction the
            scenario is facing — the value updates as you drag.
          </Text>

          <View>
            <Text textStyle="xs/regular" color="textMuted">
              Friendly name
            </Text>
            <TextInput
              value={draftName}
              onChangeText={setDraftName}
              placeholder={editingRow.backendScenarioName}
              style={{
                borderWidth: 1,
                borderColor: theme.colors.borderCard,
                borderRadius: theme.spacing.md,
                paddingHorizontal: theme.spacing.lg,
                paddingVertical: theme.spacing.md,
                color: theme.colors.text,
              }}
            />
          </View>

          <View flexDirection="row" gap="lg">
            <View flex={1}>
              <Text textStyle="xs/regular" color="textMuted">
                Latitude
              </Text>
              <TextInput
                value={draftLat}
                onChangeText={setDraftLat}
                placeholder="-33.8688"
                style={{
                  borderWidth: 1,
                  borderColor: theme.colors.borderCard,
                  borderRadius: theme.spacing.md,
                  paddingHorizontal: theme.spacing.lg,
                  paddingVertical: theme.spacing.md,
                  color: theme.colors.text,
                }}
                inputMode="decimal"
              />
            </View>
            <View flex={1}>
              <Text textStyle="xs/regular" color="textMuted">
                Longitude
              </Text>
              <TextInput
                value={draftLng}
                onChangeText={setDraftLng}
                placeholder="151.2093"
                style={{
                  borderWidth: 1,
                  borderColor: theme.colors.borderCard,
                  borderRadius: theme.spacing.md,
                  paddingHorizontal: theme.spacing.lg,
                  paddingVertical: theme.spacing.md,
                  color: theme.colors.text,
                }}
                inputMode="decimal"
              />
            </View>
            <View flex={1}>
              <Text textStyle="xs/regular" color="textMuted">
                Direction (°)
              </Text>
              <TextInput
                value={draftDir}
                onChangeText={setDraftDir}
                placeholder="0"
                style={{
                  borderWidth: 1,
                  borderColor: theme.colors.borderCard,
                  borderRadius: theme.spacing.md,
                  paddingHorizontal: theme.spacing.lg,
                  paddingVertical: theme.spacing.md,
                  color: theme.colors.text,
                }}
                inputMode="numeric"
              />
            </View>
          </View>

          <View flexDirection="row" gap="lg" justifyContent="flex-end">
            <Pressable
              onPress={cancelEdit}
              style={{
                paddingHorizontal: theme.spacing["3xl"],
                paddingVertical: theme.spacing.md,
                borderWidth: 1,
                borderColor: theme.colors.borderCard,
                borderRadius: theme.spacing.md,
              }}
            >
              <Text textStyle="sm/regular" color="text">
                Cancel
              </Text>
            </Pressable>
            <Pressable
              onPress={saveEdit}
              disabled={isPending}
              style={{
                paddingHorizontal: theme.spacing["3xl"],
                paddingVertical: theme.spacing.md,
                backgroundColor: theme.colors.icon,
                borderRadius: theme.spacing.md,
                opacity: isPending ? 0.5 : 1,
              }}
            >
              <Text textStyle="sm/semibold" color="background">
                {isPending ? "Saving…" : "Save"}
              </Text>
            </Pressable>
          </View>
        </View>
      )}

      {/* Un-positioned list */}
      {unpositioned.length > 0 && (
        <View
          style={{
            maxHeight: 180,
            borderRadius: theme.spacing.md,
            borderWidth: 1,
            borderColor: theme.colors.borderCard,
            overflow: "hidden",
          }}
        >
          <View
            style={{
              paddingHorizontal: theme.spacing.xl,
              paddingVertical: theme.spacing.md,
              backgroundColor: theme.colors.surfaceCard,
              borderBottomWidth: 1,
              borderBottomColor: theme.colors.borderCard,
            }}
          >
            <Text textStyle="xs/semibold" color="textMuted">
              {unpositioned.length} scenario{unpositioned.length === 1 ? "" : "s"} need a pin
            </Text>
          </View>
          <ScrollView style={{ flex: 1 }}>
            {unpositioned.map((row) => (
              <View
                key={row.scenarioUuid}
                flexDirection="row"
                alignItems="center"
                style={{
                  paddingHorizontal: theme.spacing.xl,
                  paddingVertical: theme.spacing.lg,
                  borderBottomWidth: 1,
                  borderBottomColor: theme.colors.borderCard,
                  gap: theme.spacing.md,
                }}
              >
                <Icon name="motion" size={theme.spacing["4xl"]} color={theme.colors.textMuted} />
                <View flex={1}>
                  <Text textStyle="sm/regular" color="text" numberOfLines={1}>
                    {row.scenarioName}
                  </Text>
                  <Text textStyle="xs/regular" color="textMuted" numberOfLines={1}>
                    {row.cameraDisplayName}
                  </Text>
                </View>
                <Pressable
                  onPress={() => beginEdit(row)}
                  style={{
                    paddingHorizontal: theme.spacing.lg,
                    paddingVertical: theme.spacing.xs,
                    borderRadius: theme.spacing.md,
                    borderWidth: 1,
                    borderColor: theme.colors.borderCard,
                  }}
                >
                  <Text textStyle="xs/regular" color="text">
                    Configure
                  </Text>
                </Pressable>
              </View>
            ))}
          </ScrollView>
        </View>
      )}

      {/* Positioned list */}
      {positioned.length > 0 && (
        <View
          style={{
            maxHeight: 160,
            borderRadius: theme.spacing.md,
            borderWidth: 1,
            borderColor: theme.colors.borderCard,
            overflow: "hidden",
          }}
        >
          <ScrollView style={{ flex: 1 }}>
            {positioned.map((row) => {
              const c = configByUuid[row.scenarioUuid]?.coordinate;
              if (!c) return null;
              return (
                <View
                  key={row.scenarioUuid}
                  flexDirection="row"
                  alignItems="center"
                  style={{
                    paddingHorizontal: theme.spacing.xl,
                    paddingVertical: theme.spacing.lg,
                    borderBottomWidth: 1,
                    borderBottomColor: theme.colors.borderCard,
                    gap: theme.spacing.md,
                    backgroundColor:
                      selectedScenarioUuid === row.scenarioUuid ? theme.colors.background : "transparent",
                  }}
                >
                  <Icon name="motion" size={theme.spacing["4xl"]} color={theme.colors.icon} />
                  <View flex={1}>
                    <Text textStyle="sm/regular" color="text" numberOfLines={1}>
                      {row.scenarioName}
                    </Text>
                    <Text textStyle="xxs/regular" color="textMuted" numberOfLines={1}>
                      {c.lat.toFixed(4)}, {c.lng.toFixed(4)} · {row.cameraDisplayName}
                    </Text>
                  </View>
                  <Pressable onPress={() => beginEdit(row)} style={{ padding: theme.spacing.xs }}>
                    <Text textStyle="xs/regular" color="textMuted">
                      Edit
                    </Text>
                  </Pressable>
                  <Pressable
                    onPress={async () => {
                      try {
                        await putConfig(row.siteId, row.assetCameraKey, row.scenarioUuid, { coordinate: null });
                        if (editingUuid === row.scenarioUuid) cancelEdit();
                      } catch {
                        // mutation error state surfaces via isPending/error
                      }
                    }}
                    disabled={isPending}
                    style={{ padding: theme.spacing.xs, opacity: isPending ? 0.5 : 1 }}
                  >
                    <Text textStyle="xs/regular" color="danger">
                      Remove
                    </Text>
                  </Pressable>
                </View>
              );
            })}
          </ScrollView>
        </View>
      )}
    </View>
  );
}
